import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Mail, Phone, Send } from 'lucide-react';
import { toast } from 'sonner';

const NewsletterPopup = () => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Function to send subscription data to Google Sheets
  async function subscribe(email, whatsapp) {
    console.log('Sending subscription data:', { email, whatsapp });
    
    const scriptUrl = "https://script.google.com/macros/s/AKfycbz_-QiW83CDHdwWt5bqGhBRHfuxlQ_RYIC2WHA6zROEV0q5ocK2mwD7UmnJqj9VCAk/exec";
    
    // Use form data to avoid CORS preflight (application/x-www-form-urlencoded is a "simple" request)
    const formData = new FormData();
    formData.append('email', email);
    formData.append('whatsapp', whatsapp);
    
    try {
      const response = await fetch(scriptUrl, {
        method: "POST",
        body: formData
      });
      
      console.log('Response status:', response.status);
      const result = await response.json();
      console.log('Response data:', result);
      return result;
    } catch (error) {
      console.warn('Fetch error:', error);
      // If fetch fails, the data might still have been saved
      // Google Apps Script sometimes has issues returning responses
      console.log('Request may have succeeded despite error');
    }
  }

  useEffect(() => {
    // Check if user has already subscribed
    const hasSubscribed = localStorage.getItem('newsletter_subscribed');
    
    // If user has subscribed, don't show popup
    if (hasSubscribed === 'true') {
      return;
    }

    // Show popup after 3 seconds on every visit (unless subscribed)
    const timer = setTimeout(() => {
      setOpen(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!email || !whatsapp) {
      toast.error('Please fill in both email and WhatsApp number');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    // WhatsApp number validation (basic - should start with + and have digits)
    const phoneRegex = /^\+?[1-9]\d{1,14}$/;
    const cleanPhone = whatsapp.replace(/\s|-|\(|\)/g, '');
    if (!phoneRegex.test(cleanPhone)) {
      toast.error('Please enter a valid WhatsApp number (e.g., +1234567890)');
      return;
    }

    setLoading(true);
    setIsSubmitting(true);

    try {
      // Send subscription data to Google Sheets
      await subscribe(email, cleanPhone);

      // Store subscription in localStorage to prevent showing popup again
      const subscriptionData = {
        email,
        whatsapp: cleanPhone,
        timestamp: new Date().toISOString()
      };
      localStorage.setItem('newsletter_subscribed', 'true');
      localStorage.setItem('newsletter_data', JSON.stringify(subscriptionData));

      toast.success('Thank you for subscribing! You\'ll receive our latest updates.');
      setEmail('');
      setWhatsapp('');
      setOpen(false);
    } catch (error) {
      console.error('Error subscribing to newsletter:', error);
      toast.error('Something went wrong. Please try again later.');
    } finally {
      setLoading(false);
      setIsSubmitting(false);
    }
  };

  const handleDismiss = () => {
    // Simply close the popup - it will show again on next visit
    setOpen(false);
  };

  const handleOpenChange = (isOpen) => {
    if (!isOpen && !isSubmitting) {
      // Only dismiss if user manually closed (not after successful submission)
      handleDismiss();
    } else {
      setOpen(isOpen);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-forest-green flex items-center gap-2">
            <Mail className="w-6 h-6 text-warm-amber" />
            Stay Connected with Us!
          </DialogTitle>
          <DialogDescription className="text-base pt-2">
            Subscribe to our newsletter and get updates on rescue stories, events, and ways to help animals in need.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="newsletter-email" className="text-sm font-medium text-gray-700">
              Email Address *
            </Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                id="newsletter-email"
                type="email"
                placeholder="your.email@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="newsletter-whatsapp" className="text-sm font-medium text-gray-700">
              WhatsApp Number *
            </Label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                id="newsletter-whatsapp"
                type="tel"
                placeholder="+1234567890"
                value={whatsapp}
                onChange={(e) => setWhatsapp(e.target.value)}
                className="pl-10"
                required
              />
            </div>
            <p className="text-xs text-gray-500">
              Include country code (e.g., +1 for USA, +91 for India)
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Button
              type="submit"
              className="flex-1 bg-warm-amber hover:bg-amber-600 text-white"
              disabled={loading}
            >
              {loading ? (
                'Subscribing...'
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Subscribe Now
                </>
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleDismiss}
              className="flex-1"
              disabled={loading}
            >
              Maybe Later
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default NewsletterPopup;
